/**
 * BUG-1418 — the ONE operator sentence for each auction refusal reason.
 *
 * Both surfaces that answer an `AuctionBlockReason` read `auctionRefusalCopy`: the disabled
 * row in the admin status dropdown (`packages/admin-frontend/.../auction-status-refusal-copy.ts`)
 * and the 4xx from `POST /api/auctions/[id]/transition`. Before this card each
 * kept its own `Record<AuctionBlockReason, …>`, and they disagreed on 6 of the
 * 11 reasons — an operator who hit the same wall two ways (greyed row, then a
 * direct POST) was told two different things about it.
 *
 * **Why it lives in `core/services/lifecycle` and not beside either caller.**
 * `app/api/**` importing `components/**` is a `routes -> frontend` layer
 * inversion the BL-1593 edge checker rejects — code flows lib → components →
 * app, never back up. The layer BOTH sides already depend on is the only place
 * one definition is legal. `AUCTION_NO_ELIGIBLE_LOTS_MESSAGE` was moved here by
 * BUG-1479 for exactly this reason, one reason at a time; this map is that
 * decision applied to the whole vocabulary, and it absorbed that constant.
 *
 * **What this map is NOT.** It is the shared *fallback*, not the whole answer.
 * The dropdown resolves through three layers, most specific first — a
 * per-`(source → target)` override (`PHASE_PATH_COPY`), then the detail
 * describers that spend the line on live counts (`openLotCount`,
 * `deleteBlockers`, `readyBlockers`), then this. The route has the same shape
 * with one rung: a `preferDetail` entry renders the thrower's own sentence and
 * falls back here. Merging the maps deliberately did NOT flatten those rungs —
 * a counted line beats a generic one on both sides.
 *
 * **Copy rule (rule #38 §6): a refused row names the FIX, not the refusal.**
 * Where a fix exists the sentence is an imperative naming it. Where none exists
 * — nothing can make a Draft auction pausable — the sentence states the
 * auction's actual condition instead, because an imperative would be a lie.
 * That split is why the adjudication went four-two rather than one way.
 */
import type { AuctionBlockReason } from './auction-transition-guard';
import { closedLabelInSentence, houseClosedLabel, type CloseStageLabelSource } from './close-stage-label';

/**
 * BL-2282 — the two reasons whose sentence NAMES the closed state, and so reads
 * the house's Closed label (AC 3 / AC 6). Every other reason is label-blind.
 */
type LabelledRefusalReason = 'close-stage-set' | 'not-reopenable';

/** The house context a refusal sentence may name: its Closed label (`null` = the default word). */
export type AuctionRefusalCopyContext = Pick<CloseStageLabelSource, 'closedLabel'>;

/**
 * The label-blind half of the copy, keyed on the reason the SERVER answered with.
 * Exhaustive over the rest of {@link AuctionBlockReason}: a new reason fails to
 * compile until someone writes its line, on both surfaces at once.
 */
const STATIC_REFUSAL_COPY: Record<Exclude<AuctionBlockReason, LabelledRefusalReason>, string> = {
  // — Reasons with a fix: name it. —
  deleted: 'Restore this auction first.',
  'open-lots': 'Close or resolve the auction’s open lots first.',
  'not-ready': 'Finish the go-live requirements first.',
  'delete-blocked': 'Resolve the auction’s lots and bids first.',
  'no-eligible-lots': 'There is nothing to open. Mark at least one lot Upcoming first.',
  // BL-2278 — Draft lands the auction exactly where un-closing would, and
  // Upcoming runs the go-live gate that un-closing would skip.
  'never-opened': 'This auction never opened. Move it to Draft or Upcoming instead.',
  // BL-2279 — only a closed auction has post-close stages.
  'not-closed': 'Close the auction first.',

  // — Reasons with no fix: state the condition. An imperative here would be a
  //   lie, because nothing the operator can do makes the move available. —
  'already-there': 'The auction is already in this status.',
  'not-pausable': 'This auction is not running, so there is nothing to pause or resume.',
  'not-restorable': 'Only a deleted auction can be restored.',
  // BL-2281 — naming a stage is Auctogon staff's job (Super Admin), so the
  // house operator has no fix to be told about.
  'stage-not-configured': 'This house has no closing stage at that position.',
  // Deliberately generic. This reason fires for `mark-pre-bidding` and
  // `mark-ready` too, not just `open` — the route's retired line said "cannot be
  // opened from its current status", which named a transition the operator may
  // not have attempted.
  'not-openable': 'This auction cannot reach that status from where it is now.',
};

/**
 * The refusal sentence an operator sees for the reason the SERVER answered with.
 *
 * BL-2282 replaced the `Record` this module exported with this function: the two
 * reasons that name the closed state read the house's Closed label — the status
 * name for `close-stage-set` (BL-2280's "Move the auction back to Closed first."
 * on a default house) and the in-sentence form for `not-reopenable` ("not
 * closed" on a default house). A house with no label reads today's copy exactly.
 *
 * @param reason - The guard's block reason.
 * @param ctx - The house's Closed label (`null` = the default word).
 * @returns The operator sentence.
 */
export function auctionRefusalCopy(reason: AuctionBlockReason, ctx: AuctionRefusalCopyContext): string {
  switch (reason) {
    // BL-2280 (PRJ-89 D3, §7al) — a staged auction's exits (reopen, Draft,
    // Upcoming, Delete) all wait on the one move back to plain Closed.
    case 'close-stage-set':
      return `Move the auction back to ${houseClosedLabel(ctx)} first.`;
    case 'not-reopenable':
      return `This auction is not ${closedLabelInSentence(ctx)}, so there is nothing to re-open.`;
    default:
      return STATIC_REFUSAL_COPY[reason];
  }
}

/**
 * Singular/plural nouns for the `SoftDeleteService` restrict-blocker kinds
 * (`AuctionRepository.countDeleteBlockersForMany`), in the order they read best.
 *
 * Deliberately NOT an allowlist — see {@link describeAuctionDeleteBlockers}.
 */
const DELETE_BLOCKER_NOUNS: Record<string, { one: string; many: string }> = {
  lots: { one: 'lot', many: 'lots' },
  bids: { one: 'bid', many: 'bids' },
};

/**
 * BL-2280 — blocker keys that are NOT attached rows, so never a noun in the
 * sentence. `closeStage` is the auction's post-close stage: a staged auction is
 * refused as `close-stage-set` (its own copy), never as "N closeStages are still
 * attached". The one deliberate exception to "unknown kinds are rendered".
 */
const NON_ROW_BLOCKER_KEYS: ReadonlySet<string> = new Set(['closeStage']);

/**
 * BL-2280 — the attached-row blockers only: `blockers` without the
 * {@link NON_ROW_BLOCKER_KEYS}. The transition-check API passes its
 * `deleteBlockers` through this, so the stage the soft-delete graph counts for
 * its own refusal never reaches the response (whose shape stays `{ lots, bids }`).
 *
 * @param blockers Restrict-blocker counts by kind, as `SoftDeleteService` reports them.
 * @returns A new map without the non-row keys.
 * @sideEffect none — pure.
 */
export function auctionDeleteRowBlockers(blockers: Record<string, number>): Record<string, number> {
  return Object.fromEntries(Object.entries(blockers).filter(([k]) => !NON_ROW_BLOCKER_KEYS.has(k)));
}

/** `['a']` → `a` · `['a','b']` → `a and b` · `['a','b','c']` → `a, b and c`. */
function joinClauses(clauses: readonly string[]): string {
  if (clauses.length <= 1) return clauses[0] ?? '';
  return `${clauses.slice(0, -1).join(', ')} and ${clauses[clauses.length - 1]}`;
}

/**
 * BL-1415 / AC 5 — the soft-delete refusal, naming each restrict blocker with
 * its count.
 *
 * **Moved here from the dropdown by BUG-1418, and now has two callers.** The
 * greyed Deleted row builds it from the CHECK API's `deleteBlockers`; the
 * transition route builds it from the counts `SoftDeleteService` returns when
 * the WRITE is refused. Both are the same `Record<kind, count>` reported by the
 * same service, so one function means the row and the 400 count the same rows
 * in the same words. Before this, the write path assembled `lots=1, bids=1` and
 * then threw it away unrendered.
 *
 * **Unknown kinds are rendered, not filtered.** The input is typed
 * `Record<string, number>` and `SoftDeleteService` can gain a restrict edge
 * without this file knowing. Dropping an unrecognised key would tell the
 * operator a delete is blocked by nothing; the raw key is a table name and reads
 * acceptably ("4 consignments"), so it is used as-is.
 *
 * @param blockers Restrict-blocker counts by kind, as the service reports them.
 * @returns The counted sentence, or `null` when no blocker survived the
 *   zero-filter — in which case the caller falls back to the shared line.
 * @sideEffect none — pure.
 */
export function describeAuctionDeleteBlockers(
  blockers: Record<string, number> | null | undefined,
): string | null {
  if (!blockers) return null;
  // Known kinds first in their declared order, then anything new, so the
  // sentence is stable across responses whose key order differs.
  const keys = [
    ...Object.keys(DELETE_BLOCKER_NOUNS).filter((k) => k in blockers),
    ...Object.keys(blockers).filter((k) => !(k in DELETE_BLOCKER_NOUNS) && !NON_ROW_BLOCKER_KEYS.has(k)),
  ];

  const clauses: string[] = [];
  let total = 0;
  for (const key of keys) {
    const n = blockers[key];
    // Explicit `> 0` per clause, never truthiness — `0` is the common value and
    // the whole point of this filter is that it must be SILENT (rule #4).
    if (n == null || n <= 0) continue;
    total += n;
    const noun = DELETE_BLOCKER_NOUNS[key] ?? { one: key.replace(/s$/, ''), many: key };
    clauses.push(`${n} ${n === 1 ? noun.one : noun.many}`);
  }
  if (clauses.length === 0) return null;

  return total === 1
    ? `${joinClauses(clauses)} is still attached. Resolve it before deleting.`
    : `${joinClauses(clauses)} are still attached. Resolve them before deleting.`;
}
